/**
 * IMU trace — `DeviceMotionEvent` sampling while a clip records (FSD §4.6; geo leg G8's client
 * half: IMU ↔ optical-flow coherence is computed SERVER-side; this module only records what the
 * device reported and summarises it per chunk into the X-SV-IMU header string).
 *
 * The header string is bound into the hash chain verbatim, so it is built DETERMINISTICALLY:
 * fixed key order, 3-decimal rounding, no whitespace — `imuHeader()` is the one function that
 * produces it, on both sides of any comparison.
 *
 * iOS 13+ requires `DeviceMotionEvent.requestPermission()` from a user gesture; Android/desktop
 * fire without asking. `requestMotionPermission()` reports which case applied so the permission
 * state reaches telemetry honestly.
 */

export interface ImuSample {
  readonly t: number;
  readonly ax: number;
  readonly ay: number;
  readonly az: number;
  readonly rx: number;
  readonly ry: number;
  readonly rz: number;
}

export interface ImuSummary {
  readonly samples: number;
  readonly windowMs: number;
  /** RMS deviation of |a| from 1 g (m/s²) — motion energy. */
  readonly accelRms: number;
  /** RMS of the rotation-rate magnitude (deg/s). */
  readonly gyroRms: number;
  readonly accelPeak: number;
  /** Fraction of samples that look motionless (a phone on a tripod, or a replay). */
  readonly stillRatio: number;
}

const G = 9.80665;
const STILL_ACCEL = 0.3;
const STILL_GYRO = 5;

const r3 = (v: number): number => Math.round(v * 1000) / 1000;

export function summariseImu(samples: readonly ImuSample[], fromT: number, toT: number): ImuSummary | null {
  const win = samples.filter((s) => s.t >= fromT && s.t <= toT);
  if (win.length === 0) return null;
  let accSq = 0;
  let gyrSq = 0;
  let peak = 0;
  let still = 0;
  for (const s of win) {
    const a = Math.abs(Math.hypot(s.ax, s.ay, s.az) - G);
    const g = Math.hypot(s.rx, s.ry, s.rz);
    accSq += a * a;
    gyrSq += g * g;
    if (a > peak) peak = a;
    if (a < STILL_ACCEL && g < STILL_GYRO) still += 1;
  }
  const n = win.length;
  return {
    samples: n,
    windowMs: Math.max(0, Math.round(toT - fromT)),
    accelRms: r3(Math.sqrt(accSq / n)),
    gyroRms: r3(Math.sqrt(gyrSq / n)),
    accelPeak: r3(peak),
    stillRatio: r3(still / n),
  };
}

/** The exact X-SV-IMU header string (key order fixed; numbers already rounded). */
export function imuHeader(s: ImuSummary): string {
  return (
    `{"samples":${s.samples},"windowMs":${s.windowMs},"accelRms":${s.accelRms},` +
    `"gyroRms":${s.gyroRms},"accelPeak":${s.accelPeak},"stillRatio":${s.stillRatio}}`
  );
}

export type MotionPermission = 'granted' | 'denied' | 'not-required' | 'unsupported';

type RequestingMotion = typeof DeviceMotionEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

/**
 * True where `DeviceMotionEvent.requestPermission()` exists (iOS 13+): the request must then run inside
 * a user gesture. Everywhere else motion fires without asking and may be enabled from an effect.
 */
export function motionPermissionNeedsGesture(): boolean {
  if (typeof window === 'undefined' || typeof DeviceMotionEvent === 'undefined') return false;
  return typeof (DeviceMotionEvent as RequestingMotion).requestPermission === 'function';
}

export async function requestMotionPermission(): Promise<MotionPermission> {
  if (typeof window === 'undefined' || typeof DeviceMotionEvent === 'undefined') return 'unsupported';
  const dme = DeviceMotionEvent as RequestingMotion;
  if (typeof dme.requestPermission !== 'function') return 'not-required';
  try {
    return (await dme.requestPermission()) === 'granted' ? 'granted' : 'denied';
  } catch {
    return 'denied';
  }
}

/** A bounded ring of motion samples (≈ 2 minutes at 60 Hz) with per-window summaries. */
export class ImuTracer {
  private samples: ImuSample[] = [];
  private listener: ((e: DeviceMotionEvent) => void) | null = null;
  private readonly cap: number;
  permission: MotionPermission = 'unsupported';

  constructor(cap = 7200, private readonly clock: () => number = () => Date.now()) {
    this.cap = cap;
  }

  /** Attach the listener. Call AFTER `requestMotionPermission()` on iOS. */
  start(): void {
    if (this.listener || typeof window === 'undefined') return;
    this.listener = (e: DeviceMotionEvent) => {
      const a = e.accelerationIncludingGravity;
      const r = e.rotationRate;
      this.push({
        t: this.clock(),
        ax: a?.x ?? 0,
        ay: a?.y ?? 0,
        az: a?.z ?? 0,
        rx: r?.alpha ?? 0,
        ry: r?.beta ?? 0,
        rz: r?.gamma ?? 0,
      });
    };
    window.addEventListener('devicemotion', this.listener);
  }

  /** Test seam / synthetic feed. */
  push(s: ImuSample): void {
    this.samples.push(s);
    if (this.samples.length > this.cap) this.samples.splice(0, this.samples.length - this.cap);
  }

  stop(): void {
    if (this.listener && typeof window !== 'undefined') {
      window.removeEventListener('devicemotion', this.listener);
    }
    this.listener = null;
  }

  get sampleCount(): number {
    return this.samples.length;
  }

  window(fromT: number, toT: number): ImuSummary | null {
    return summariseImu(this.samples, fromT, toT);
  }

  /** Whole-trace summary for the submit-time telemetry. */
  all(): ImuSummary | null {
    if (this.samples.length === 0) return null;
    const first = this.samples[0] as ImuSample;
    const last = this.samples[this.samples.length - 1] as ImuSample;
    return summariseImu(this.samples, first.t, last.t);
  }
}
